import styled from 'styled-components';
import { type FC, useState } from 'react';
import { Icon, IconType } from '../Props/Icon/Icon';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import {
  type AggregatedScoreBreakdown,
  ScoreBreakdownCategories,
  type ScoreBreakdownContentEntryKey,
  useScoreBreakdownByUserQuery,
} from '../../apiHooks/useScoreBreakdownByUserQuery';
import { StatusIcon, StatusIconType } from '../Props/StatusIcon/StatusIcon';
import { NoUserDataCard } from './components/NoUserDataCard';
import { DynamicActionsSection } from './components/DynamicActionsSection';
import { ActivityLogModal } from './components/ActivityLogModal';
import type { ActivityOverviewWidgetParams } from '../Activity/ActivityOverviewWidget';
import { GroupingType, TimeRange } from '../../apiHooks/useUserScoreTimelineQuery';
import { formatNumberWithCommas } from '../../lib/numbers/formatNumberWithComma';
import { behaviorIcons } from './utils/behaviorIcons';

type ActivityLogModalProps = {
  isOpen: boolean;
  params?: ActivityOverviewWidgetParams;
};

type TabKey = 'behaviors' | 'recentInsights';

const tabs: { key: TabKey; label: string }[] = [
  { key: 'behaviors', label: 'Your Behaviors' },
  { key: 'recentInsights', label: 'Recent Insights' },
];

const SCORE_BREAKDOWN_CONTENT_ENTRY_MAP: Record<ScoreBreakdownCategories, ScoreBreakdownContentEntryKey> = {
  [ScoreBreakdownCategories.COMPLETION]: 'scoreBreakdownCompletion',
  [ScoreBreakdownCategories.ENGAGEMENT]: 'scoreBreakdownEngagement',
  [ScoreBreakdownCategories.EMAIL_SECURITY]: 'scoreBreakdownEmailSecurity',
  [ScoreBreakdownCategories.KNOWLEDGE]: 'scoreBreakdownKnowledge',
  [ScoreBreakdownCategories.DATA_PROTECTION]: 'scoreBreakdownDataProtection',
  [ScoreBreakdownCategories.MICROSOFT_IDENTITY]: 'scoreBreakdownMicrosoftIdentity',
  [ScoreBreakdownCategories.PHISHING_REPORTS]: 'scoreBreakdownPhishingReports',
  [ScoreBreakdownCategories.PHISHING_CLICKS]: 'scoreBreakdownPhishingClicks',
};

const SCORE_BREAKDOWN_CATEGORY_ALIASES: Record<string, ScoreBreakdownCategories> = {
  completion: ScoreBreakdownCategories.COMPLETION,
  'training & compliance': ScoreBreakdownCategories.COMPLETION,
  'training and compliance': ScoreBreakdownCategories.COMPLETION,

  engagement: ScoreBreakdownCategories.ENGAGEMENT,
  'general engagement': ScoreBreakdownCategories.ENGAGEMENT,
  'email security': ScoreBreakdownCategories.EMAIL_SECURITY,

  knowledge: ScoreBreakdownCategories.KNOWLEDGE,
  'data protection': ScoreBreakdownCategories.DATA_PROTECTION,
  'microsoft identity': ScoreBreakdownCategories.MICROSOFT_IDENTITY,

  phishingreports: ScoreBreakdownCategories.PHISHING_REPORTS,
  phishingclicks: ScoreBreakdownCategories.PHISHING_CLICKS,
  'phishing & social engineering': ScoreBreakdownCategories.PHISHING_REPORTS,
  'phishing and social engineering': ScoreBreakdownCategories.PHISHING_REPORTS,
};

const normalizeScoreBreakdownCategory = (scoreBreakdown: string) => scoreBreakdown.trim().toLowerCase();

const resolveScoreBreakdownCategory = (scoreBreakdown: string): ScoreBreakdownCategories | undefined => {
  return SCORE_BREAKDOWN_CATEGORY_ALIASES[normalizeScoreBreakdownCategory(scoreBreakdown)];
};

const getSentiment = (item: AggregatedScoreBreakdown): 'Positive' | 'Neutral' | 'Negative' => {
  if (item.score > 0) {
    return 'Positive';
  }
  if (item.score < 0) {
    return 'Negative';
  }

  return 'Neutral';
};

type Sentiment = 'Positive' | 'Neutral' | 'Negative';

const STATUS_ICON_BY_SENTIMENT: Record<Sentiment, StatusIconType> = {
  Positive: StatusIconType.SUCCESS,
  Neutral: StatusIconType.NEUTRAL,
  Negative: StatusIconType.FAIL,
};

const ICON_COLOR_BY_SENTIMENT: Record<Sentiment, string> = {
  Positive: '#267D5C',
  Neutral: '#606B85',
  Negative: '#DF163E',
};

const formatScore = (score: number): string => {
  if (score > 0) {
    return `+${formatNumberWithCommas(score)}`;
  }

  return formatNumberWithCommas(score);
};

export const ScoreBreakdown: FC = () => {
  const scoreBreakdownByUserResponse = useScoreBreakdownByUserQuery();
  // const isMobile = useMediaQuery('(max-width: 768px)');
  const [activeTab, setActiveTab] = useState<TabKey>('behaviors');
  const [modalProps, setModalProps] = useState<ActivityLogModalProps>({
    isOpen: false,
    params: undefined,
  });

  const scoreBreakdownAggregations: AggregatedScoreBreakdown[] =
    scoreBreakdownByUserResponse.data?.scoreBreakdownAggregations ?? [];

  const dynamicActionAggregations = scoreBreakdownByUserResponse.data?.dynamicActionAggregations ?? [];

  const getContentEntryKeyByScoreBreakdown = (
    item: AggregatedScoreBreakdown,
  ): ScoreBreakdownContentEntryKey | undefined => {
    const mappedCategory = resolveScoreBreakdownCategory(item.scoreBreakdown);

    return mappedCategory ? SCORE_BREAKDOWN_CONTENT_ENTRY_MAP[mappedCategory] : undefined;
  };

  const handleViewFullLog = (item: AggregatedScoreBreakdown) => {
    const mappedCategory = resolveScoreBreakdownCategory(item.scoreBreakdown);

    setModalProps({
      isOpen: true,
      params: {
        scoreBreakdowns: mappedCategory ? [mappedCategory] : [],
        groupingType: GroupingType.MONTH,
        timeRange: TimeRange.ONE_YEAR,
      },
    });
  };

  return (
    <Container>
      {/* <TitleContainer>
        <Spread>
          <Title>
            <NamedContentEntryText container={'span'} refKey="sectionTitles" subKey="scoreBreakdown" />
            <NamedContentEntryText container={'label'} refKey="sectionTitles" subKey="last30Days" />
          </Title>
          <ViewActivityLogButton onClick={() => setModalProps({ isOpen: true, params: undefined })}>
            <NamedContentEntryText container={'span'} refKey="scoreBreakdownTooltips" subKey="viewActivityLog" />
            {!isMobile && (
              <Tooltip content={<ViewActivityLogTooltip />}>
                <Icon icon={IconType.INFO_TOOLTIP} width={16} height={16} fill="transparent" />
              </Tooltip>
            )}
          </ViewActivityLogButton>
        </Spread>
      </TitleContainer> */}
      <TabBar>
        {tabs.map((t) => (
          <TabButton key={t.key} $active={activeTab === t.key} onClick={() => setActiveTab(t.key)}>
            {t.label}
          </TabButton>
        ))}
      </TabBar>
      {activeTab === 'behaviors' && (
        <>
          {!!scoreBreakdownAggregations.length && (
            <ScoreList>
              {scoreBreakdownAggregations.map((item) => {
                const sentiment = getSentiment(item);
                const contentEntryKey = getContentEntryKeyByScoreBreakdown(item);
                const mappedCategory = resolveScoreBreakdownCategory(item.scoreBreakdown);
                const BehaviorIcon = mappedCategory ? behaviorIcons[mappedCategory] : undefined;

                return (
                  <ScoreRow
                    key={item.scoreBreakdown}
                    type="button"
                    onClick={() => handleViewFullLog(item)}
                    aria-label={item.scoreBreakdown}
                  >
                    <ScoreRowIcon $color={ICON_COLOR_BY_SENTIMENT[sentiment]}>
                      {BehaviorIcon ? (
                        <BehaviorIcon width={16} height={16} />
                      ) : (
                        item.assetsUrl && <img src={item.assetsUrl} alt="" />
                      )}
                    </ScoreRowIcon>
                    <ScoreRowTitle>
                      {contentEntryKey ? (
                        <NamedContentEntryText
                          refKey={contentEntryKey}
                          container={'span'}
                          subKey={`heading${sentiment}`}
                        />
                      ) : (
                        <span>{item.scoreBreakdown}</span>
                      )}
                    </ScoreRowTitle>
                    <ScoreRowStatus>
                      <StatusIcon icon={STATUS_ICON_BY_SENTIMENT[sentiment]} height={16} width={16} />
                    </ScoreRowStatus>
                    <ScoreRowValue>{formatScore(item.score)}</ScoreRowValue>
                    <ScoreRowChevron className="rtl-flip">
                      <Icon icon={IconType.CHEVRON_RIGHT} width={14} height={14} fill={'transparent'} />
                    </ScoreRowChevron>
                  </ScoreRow>
                );
              })}
            </ScoreList>
          )}
          {!scoreBreakdownAggregations.length && <NoUserDataCard />}
        </>
      )}
      {activeTab === 'recentInsights' &&
        (dynamicActionAggregations.length > 0 ? (
          <DynamicActionsSection items={dynamicActionAggregations} hideTitle />
        ) : (
          <NoUserDataCard />
        ))}
      <ActivityLogModal
        isOpen={modalProps.isOpen}
        onClose={() => setModalProps({ isOpen: false, params: undefined })}
        params={modalProps.params}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TabBar = styled.div`
  display: inline-flex;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.prototype.border};
  padding: 3px;
  background: ${({ theme }) => theme.colors.prototype.secondary.replace(')', ' / 50%)')};
  width: fit-content;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: 6px 16px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    box-shadow 0.15s;
  background: ${({ $active, theme }) => ($active ? theme.colors.prototype.card : 'transparent')};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.prototype.foreground : theme.colors.prototype.mutedForeground};
  box-shadow: ${({ $active }) => ($active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none')};

  &:hover {
    color: ${({ theme }) => theme.colors.prototype.foreground};
  }
`;

const ScoreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ScoreRow = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: start;
  border: 1px solid ${({ theme }) => theme.colors.prototype.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.prototype.card};
  padding: 11px;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.prototype.secondary.replace(')', ' / 50%)')};
  }
`;

const ScoreRowIcon = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 12px;
  color: ${({ $color }) => $color};

  svg {
    width: 16px;
    height: 16px;
  }

  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`;

const ScoreRowTitle = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 12px;
  line-height: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.prototype.foreground};
`;

const ScoreRowStatus = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const ScoreRowValue = styled.span`
  flex-shrink: 0;
  min-width: 48px;
  text-align: right;
  font-size: 12px;
  line-height: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.prototype.foreground};
`;

const ScoreRowChevron = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
`;

// const ViewActivityLogButton = styled.button`
//   border: none;
//   background-color: transparent;
//   color: ${({ theme }) => theme.colors.prototype.mutedForeground};

//   &:hover {
//     color: ${({ theme }) => theme.colors.prototype.foreground};
//   }
//   text-decoration: underline;
//   font-size: 12px;
//   font-weight: 400;
//   cursor: pointer;
//   display: flex;
//   align-items: center;
//   gap: 8px;
// `;

// const Title = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 16px;
//   line-height: 32px;

//   > label {
//     text-transform: none;
//     color: ${({ theme }) => theme.colors.prototype.foreground};
//     font-size: 12px;
//     font-weight: 400;
//   }
// `;
